/**
 * Icon System Utilities
 * 
 * Provides utility functions for consistent icon sizing and coloring across the design system.
 * All icons inherit text color by default for maximum flexibility.
 */

/**
 * Maps size prop to Tailwind classes
 * @param {string} size - Icon size: 'xs', 'sm', 'md', 'lg', 'xl'
 * @returns {string} Tailwind size classes
 */
export const getIconSizeClass = (size = 'md') => {
  const sizeMap = {
    xs: 'w-3 h-3',      // 12px
    sm: 'w-4 h-4',      // 16px
    md: 'w-5 h-5',      // 20px
    lg: 'w-6 h-6',      // 24px
    xl: 'w-8 h-8',      // 32px
    '2xl': 'w-10 h-10', // 40px
    '3xl': 'w-12 h-12', // 48px
  };
  
  return sizeMap[size] || sizeMap.md;
};

/**
 * Maps color prop to Tailwind text color classes
 * Theme-aware colors automatically adapt to light/dark mode
 * @param {string} color - Color variant: 'inherit', 'primary', 'secondary', 'success', 'warning', 'danger', 'neutral'
 * @returns {string} Tailwind color classes
 */
export const getIconColorClass = (color = 'inherit') => {
  const colorMap = {
    inherit: '', // Inherits from parent text color
    primary: 'text-blue-600 dark:text-blue-600', // Brand primary - theme-aware
    secondary: 'text-gray-600 dark:text-dark-400', // Secondary text - theme-aware
    success: 'text-green-600 dark:text-green-500', // Success state - theme-aware
    warning: 'text-yellow-600 dark:text-yellow-500', // Warning state - theme-aware
    danger: 'text-red-600 dark:text-red-500', // Danger/error state - theme-aware
    neutral: 'text-gray-500 dark:text-dark-500', // Neutral/muted - theme-aware
    white: 'text-white dark:text-white', // White (no change)
  };
  
  return colorMap[color] || '';
};

/**
 * Combines icon classes
 * @param {string} size - Icon size
 * @param {string} color - Icon color
 * @param {string} className - Additional custom classes
 * @returns {string} Combined class string
 */
export const getIconClasses = (size = 'md', color = 'inherit', className = '') => {
  const sizeClass = getIconSizeClass(size);
  const colorClass = getIconColorClass(color);
  
  return [sizeClass, colorClass, className].filter(Boolean).join(' ');
};

/**
 * Common SVG props for all icons
 */
export const commonSvgProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  fill: 'none',
  viewBox: '0 0 24 24',
  stroke: 'currentColor',
  'aria-hidden': 'true',
};

/**
 * Creates consistent icon component wrapper
 * @param {React.Component} IconComponent - The icon SVG component
 * @param {string} iconName - Name for accessibility
 */
export const createIconComponent = (IconComponent, iconName) => {
  const Icon = ({ 
    size = 'md', 
    color = 'inherit', 
    className = '', 
    title,
    'aria-label': ariaLabel,
    ...props 
  }) => {
    const classes = getIconClasses(size, color, className);
    const accessibilityProps = title || ariaLabel ? { 'aria-hidden': false } : { 'aria-hidden': true };
    
    return (
      <IconComponent 
        className={classes}
        title={title}
        aria-label={ariaLabel || title || iconName}
        {...accessibilityProps}
        {...props}
      />
    );
  };
  
  Icon.displayName = iconName;
  return Icon;
};
